import { useEffect } from 'react';
import { startWith, takeUntil, withLatestFrom } from 'rxjs';
import { filterWidgetEvent } from '@client/foundation/shared/utils/filter-widget-event';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import { useWidget } from '@client/widgets/widget.hook';
import type { WidgetPayload } from '@client/widgets/widget.types';
import { useWindow } from '@client/window/window.hook';
import { useChartState } from '@client/state/chart-state.hook';
import { useTradingWorkspaceDestroy$ } from '@client/features/trading-workspace/trading-workspace-scope.context';
import { buildWorkspaceLayoutSnapshot } from './workspace-layout-snapshot.utils';
import { useCreateWorkspaceLayout } from './create-workspace-layout.hook';

export const useCreateWorkspaceLayoutForm = () => {
  const { openWidget$ } = useWidget();
  const { openWindow } = useWindow();
  const { getState$ } = useChartState();
  const { notify } = useNotifications();
  const workspaceDestroy$ = useTradingWorkspaceDestroy$();
  const { onCreateSubmit } = useCreateWorkspaceLayout();

  useEffect(() => {
    const subscription = openWidget$()
      .pipe(
        filterWidgetEvent<WidgetPayload, 'SaveWorkspaceLayoutForm'>(
          'SaveWorkspaceLayoutForm'
        ),
        withLatestFrom(getState$().pipe(startWith(new Map()))),
        takeUntil(workspaceDestroy$)
      )
      .subscribe(([, chartState]) => {
        if (chartState.size === 0) {
          notify({
            type: 'error',
            message: 'Open at least one chart to save workspace layout'
          });
          return;
        }

        const snapshot = buildWorkspaceLayoutSnapshot(chartState);

        openWindow({
          type: 'SaveTradingLayoutForm',
          sizePreset: 'form',
          componentProps: {
            onSubmit: onCreateSubmit(snapshot)
          }
        });
      });

    return () => subscription.unsubscribe();
  }, [
    getState$,
    notify,
    onCreateSubmit,
    openWidget$,
    openWindow,
    workspaceDestroy$
  ]);
};
